import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Skeleton } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendUp,
  faArrowTrendDown,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { motion } from "framer-motion";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import DateSelector from "../../../commonComponents/DateSelector";
import {
  SummaryCard,
  MetricsGrid,
  MetricCard,
  SummarySpace,
  HeaderSpace,
  SummaryTitle,
  SecondaryTextStyled,
  MetricsTitleSpace,
  GradientTitle,
  TrendBadge,
  TrendIcon,
  TrendText,
  MetricsWrapper,
  KeyMetricsTitle,
  MetricIconWrapper,
  MetricIconStyled,
  MetricTitleText,
  MetricValueTitle,
  MetricSubtitleText,
  MetricCardSpace,
} from "./styles";
import {
  addCommasInPrices,
  CategorySalesSummaryProps,
  KeyMetric,
  DateRange,
} from "./constants";
import { useTheme } from "../../../context/ThemeContext";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchCategorySales } from "../../../redux/CategorySales";
import styled from "styled-components";

const DateSelectorWrapper = styled.div`
  background: ${(props) => props.theme?.mode === "dark" ? "#2D3748" : "#ffffff"};
  border-radius: 8px;
  box-shadow: ${(props) => props.theme?.mode === "dark" 
    ? "0 2px 8px rgba(0, 0, 0, 0.3)"
    : "0 2px 8px rgba(0, 0, 0, 0.08)"};
  padding: 2px;
`;

const StrongText = styled.strong`
  color: ${(props) => props.theme?.mode === "dark" ? "#ffffff" : "#1A202C"};
  font-weight: 600;
`;

const CategorySalesSummary: React.FC<CategorySalesSummaryProps> = ({
  weeklyChange,
  merchant,
  storeId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  
  // Redux state
  const { categories, keyMetrics: reduxKeyMetrics, loading } = useSelector(
    (state: RootState) => state.categorySales
  );
  
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: moment("2023-01-01"),
    endDate: moment(),
  });

  const handleSelection = (selection: DateRange | { startDate: Date | null; endDate: Date | null }) => {
    let startDate: moment.Moment;
    let endDate: moment.Moment;
    
    if (moment.isMoment(selection.startDate)) {
      startDate = selection.startDate;
    } else if (selection.startDate) {
      startDate = moment(selection.startDate);
    } else {
      startDate = moment("2023-01-01");
    }
    
    if (moment.isMoment(selection.endDate)) {
      endDate = selection.endDate;
    } else if (selection.endDate) {
      endDate = moment(selection.endDate);
    } else {
      endDate = moment();
    }
    
    setSelectedDateRange({ startDate, endDate });
  };

  // Fetch data when date range, merchant, or store changes
  useEffect(() => {
    if (storeId || merchant) {
      dispatch(fetchCategorySales({
        startDate: moment(selectedDateRange.startDate)?.format("YYYY-MM-DD"),
        endDate: moment(selectedDateRange.endDate)?.format("YYYY-MM-DD"),
        merchantId: merchant?.id,
        store: storeId,
      }));
    }
  }, [selectedDateRange, merchant, storeId, dispatch]);

  // Calculate derived values
  const totalSales = useMemo(() => {
    return parseFloat(reduxKeyMetrics?.totalRevenue || "0");
  }, [reduxKeyMetrics]);

  const totalCategories = useMemo(() => {
    return categories.length || " - ";
  }, [categories]);

  // Build key metrics from Redux data
  const keyMetrics: KeyMetric[] = useMemo(() => {
    if (!categories.length || !reduxKeyMetrics) {
      return [];
    }

    const bestCategoryByRevenue = categories.reduce((prev, current) =>
      (parseFloat(String(current.totalRevenue)) || 0) >
      (parseFloat(String(prev.totalRevenue)) || 0)
        ? current
        : prev
    );

    const bestCategoryByUnitsSold = categories.reduce((prev, current) =>
      (current.totalItemsSold || 0) > (prev.totalItemsSold || 0)
        ? current
        : prev
    );

    return [
      {
        id: "totalSales",
        title: "Total Sales",
        subTitle: "Overall revenue from all categories",
        value: `$${addCommasInPrices(reduxKeyMetrics.totalRevenue)}`,
        icon: "chart-line",
        color: "#52c41a",
      },
      {
        id: "bestCategoryRevenue",
        title: "Best Category by Revenue",
        subTitle: bestCategoryByRevenue.category || "-",
        value: `$${addCommasInPrices(reduxKeyMetrics.bestCategoryByRevenue)}`,
        icon: "chart-line",
        color: "#1890ff",
      },
      {
        id: "bestCategoryUnits",
        title: "Best Category by Units Sold",
        subTitle: bestCategoryByUnitsSold.category || "-",
        value: `${reduxKeyMetrics.bestCategoryByUnitsSold} Units`,
        icon: "chart-line",
        color: "#fa8c16",
      },
      {
        id: "averageSales",
        title: "Average Revenue per Category",
        subTitle: "Average sales per category",
        value: `$${addCommasInPrices(reduxKeyMetrics.averageRevenue)}`,
        icon: "chart-line",
        color: "#722ed1",
      },
    ];
  }, [categories, reduxKeyMetrics]);

  const isIncrease = weeklyChange >= 0;
  const trendIcon = isIncrease ? faArrowTrendUp : faArrowTrendDown;
  const trendColor = isIncrease ? "#52c41a" : "#ff4d4f";

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "chart-line":
        return faChartLine;
      default:
        return faChartLine;
    }
  };

  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  return (
    <StyledThemeProvider theme={styledTheme}>
      <SummaryCard>
        <SummarySpace direction="vertical" size="large">
          <HeaderSpace align="center">
            <SummaryTitle level={4}>
              Total Category Sales
            </SummaryTitle>
            <DateSelectorWrapper>
              <DateSelector onDateSelect={handleSelection} />
            </DateSelectorWrapper>
          </HeaderSpace>

          <SecondaryTextStyled type="secondary">
            Comprehensive overview of sales across all categories.
          </SecondaryTextStyled>

        <MetricsTitleSpace align="center" size="large">
          <GradientTitle level={1}>
            ${addCommasInPrices(parseFloat(String(totalSales)).toFixed(2))}
          </GradientTitle>
          <TrendBadge align="center" isIncrease={isIncrease}>
            <TrendIcon color={trendColor}>
              <FontAwesomeIcon icon={trendIcon} />
            </TrendIcon>
            <TrendText strong color={trendColor}>
              {weeklyChange === 0 ? "0%" : `${Math.abs(weeklyChange)}%`}{" "}
              {isIncrease ? "↑" : "↓"} this week
            </TrendText>
          </TrendBadge>
        </MetricsTitleSpace>

          <SecondaryTextStyled type="secondary">
            Total Categories: <StrongText>{totalCategories}</StrongText>
          </SecondaryTextStyled>

        <MetricsWrapper>
          {loading ? (
            <Skeleton active paragraph={{ rows: 1 }} />
          ) : (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <KeyMetricsTitle level={3}>
                Key Metrics
              </KeyMetricsTitle>
            </motion.div>
          )}

          {loading ? (
            <Row gutter={[16, 16]}>
              {Array.from({ length: 4 }).map((_, index) => (
                <Col xs={24} sm={12} md={12} lg={6} key={index}>
                  <MetricCard>
                    <Skeleton active />
                  </MetricCard>
                </Col>
              ))}
            </Row>
          ) : (
            <MetricsGrid>
              {keyMetrics.map((metric) => (
                <motion.div
                  key={metric.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <MetricCard>
                    <MetricCardSpace align="center" size="middle" color={metric.color}>
                      <MetricIconWrapper color={metric.color}>
                        <MetricIconStyled color={metric.color}>
                          <FontAwesomeIcon icon={getIcon(metric.icon)} />
                        </MetricIconStyled>
                      </MetricIconWrapper>
                      <MetricTitleText strong>
                        {metric.title}
                      </MetricTitleText>
                    </MetricCardSpace>
                    <MetricValueTitle level={4} color={metric.color}>
                      {metric.value}
                    </MetricValueTitle>
                    {metric.subTitle && (
                      <MetricSubtitleText type="secondary">
                        {metric.subTitle}
                      </MetricSubtitleText>
                    )}
                  </MetricCard>
                </motion.div>
              ))}
            </MetricsGrid>
          )}
        </MetricsWrapper>
      </SummarySpace>
    </SummaryCard>
    </StyledThemeProvider>
  );
};

export default CategorySalesSummary;
